import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Compass, Ticket, QrCode, Award, CalendarX2, Heart, GraduationCap, LifeBuoy, X, ShieldAlert } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HELP_SECTIONS } from "@/lib/helpContent";
import { HelpFeedback } from "@/components/HelpFeedback";
import { useFees, fmtUF } from "@/lib/useFees";

const ICONS = { Compass, Ticket, QrCode, Award, CalendarX2, Heart, GraduationCap, ShieldAlert };
const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const Section = ({ s }) => {
  const Icon = ICONS[s.icon];
  return (
    <section id={`ayuda-${s.id}`} className="scroll-mt-24" data-testid={`help-section-${s.id}`}>
      <h2 className="font-display font-bold text-base md:text-lg flex items-center gap-2 mb-2">
        <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4" /></span>{s.title}
      </h2>
      <Accordion type="multiple" className="rounded-2xl border bg-card px-4">
        {s.items.map(([q, a], i) => (
          <AccordionItem key={q} value={`${s.id}-${i}`} className="last:border-0">
            <AccordionTrigger className="text-left text-sm sm:text-base hover:no-underline hover:text-primary" data-testid={`help-q-${s.id}-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed" data-testid={`help-a-${s.id}-${i}`}>{a}<HelpFeedback question={q} section={s.title} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

export default function HelpCenter() {
  const fees = useFees();
  const [q, setQ] = useState("");
  const sections = useMemo(() => HELP_SECTIONS({
    pub: `${fmtUF(fees?.publication_fee_uf ?? 0.07)} UF`,
  }), [fees]);
  const shown = useMemo(() => {
    const t = norm(q.trim());
    if (!t) return sections;
    return sections.map((s) => ({ ...s, items: s.items.filter(([a, b]) => norm(a + " " + b).includes(t)) })).filter((s) => s.items.length);
  }, [q, sections]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="help-center">
      <div className="mb-8">
        <p className="text-sm font-semibold text-coral flex items-center gap-1.5"><LifeBuoy className="w-4 h-4" />Centro de ayuda</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mt-2">¿En qué te ayudamos?</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">Respuestas rápidas sobre reservas, tickets, créditos, cancelaciones y cómo publicar tus talleres.</p>
        <div className="relative mt-6">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca una pregunta, por ejemplo «crédito»" className="h-12 pl-10 pr-10 rounded-xl" data-testid="help-search" />
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Limpiar" data-testid="help-search-clear"><X className="w-4 h-4" /></button>}
        </div>
        {!q && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="help-topics">
            {sections.map((s) => (
              <a key={s.id} href={`#ayuda-${s.id}`} className="rounded-full border px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary transition-colors" data-testid={`help-topic-${s.id}`}>{s.title}</a>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-8">
        {shown.map((s) => <Section key={s.id} s={s} />)}
        {shown.length === 0 && <p className="text-center text-muted-foreground py-10" data-testid="help-empty">No encontramos preguntas con «{q}». Prueba con otra palabra o escríbenos.</p>}
      </div>
      <div className="mt-12 rounded-2xl bg-primary/5 border border-primary/20 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="help-contact">
        <p className="text-sm"><strong>¿No encontraste tu respuesta?</strong> Escríbenos desde «Contactar a VIVOTaller» al final de la página.</p>
        <Button asChild variant="outline" size="sm"><Link to="/explorar">Explorar talleres</Link></Button>
      </div>
    </div>
  );
}
